// Phase 9B rebuild — Compact 7-day overview strip.
//
// Design premise (per rebuild spec §10–§11, §47):
//   * MUST fit all seven days on standard desktop widths (1440×900, 1366×768)
//     with NO horizontal scroll.
//   * Each day = one calm compact area. NEVER 7 giant KPI cards.
//   * Per day: weekday + date, number of SCHEDULED PEOPLE (not shifts),
//     and exception badges (OPEN / CONFLICT / UNPUBLISHED) — only when > 0.
//   * Today: subtle orientation signal (small primary dot). NOT a saturated card.
//   * Selected day: primary-tinted background, high-contrast text.
//   * Weekend: subtle muted differentiation.
//
// Scales identically to 100+ employees — never grows vertically.
import useTodayISO from "@/lib/useTodayISO";

const WEEKDAYS_NL = ["ma", "di", "wo", "do", "vr", "za", "zo"];

function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function WeekStrip({ days, shifts, selectedDate, onSelect, t }) {
  const todayISO = useTodayISO();
  const stats = {};
  for (const d of days) {
    const key = fmtDate(d);
    stats[key] = { employees: new Set(), open: 0, drafts: 0, byEmp: {} };
  }
  for (const s of shifts) {
    const st = stats[s.date];
    if (!st) continue;
    if (!s.employee_id) st.open += 1;
    else st.employees.add(s.employee_id);
    if (s.status !== "published") st.drafts += 1;
    if (s.employee_id) (st.byEmp[s.employee_id] = st.byEmp[s.employee_id] || []).push(s);
  }
  const toMin = (h) => { const [a, b] = (h || "00:00").split(":").map(Number); return a * 60 + (b || 0); };
  for (const key of Object.keys(stats)) {
    let conflictCount = 0;
    for (const list of Object.values(stats[key].byEmp)) {
      if (list.length < 2) continue;
      for (let i = 0; i < list.length; i++) {
        for (let j = i + 1; j < list.length; j++) {
          let aS = toMin(list[i].start_time), aE = toMin(list[i].end_time);
          let bS = toMin(list[j].start_time), bE = toMin(list[j].end_time);
          if (aE <= aS) aE += 1440; if (bE <= bS) bE += 1440;
          if (Math.max(aS, bS) < Math.min(aE, bE)) conflictCount += 1;
        }
      }
    }
    stats[key].conflicts = conflictCount;
  }

  return (
    <div
      className="grid gap-1.5 sm:gap-2 mb-4"
      style={{ gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}
      data-testid="week-strip"
      role="tablist"
    >
      {days.map((d) => {
        const key = fmtDate(d);
        const isToday = key === todayISO;
        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
        const isSelected = key === selectedDate;
        const st = stats[key] || { employees: new Set(), open: 0, drafts: 0, conflicts: 0 };
        const scheduled = st.employees.size;

        let bg, borderCol, textStrong, textMuted;
        if (isSelected) {
          bg = "hsl(var(--primary))"; borderCol = "hsl(var(--primary))";
          textStrong = "white"; textMuted = "rgba(255,255,255,0.85)";
        } else if (isToday) {
          bg = "hsl(var(--primary) / 0.05)"; borderCol = "hsl(var(--primary) / 0.30)";
          textStrong = "hsl(var(--primary))"; textMuted = "hsl(var(--text-muted))";
        } else if (isWeekend) {
          bg = "hsl(var(--muted) / 0.45)"; borderCol = "hsl(var(--border))";
          textStrong = "hsl(var(--text-strong))"; textMuted = "hsl(var(--text-muted))";
        } else {
          bg = "hsl(var(--surface))"; borderCol = "hsl(var(--border))";
          textStrong = "hsl(var(--text-strong))"; textMuted = "hsl(var(--text-muted))";
        }

        return (
          <button
            key={key}
            role="tab"
            aria-selected={isSelected}
            onClick={() => onSelect && onSelect(key)}
            data-testid={`week-strip-${key}`}
            data-selected={isSelected || undefined}
            className="text-left px-2 sm:px-3 py-2.5 rounded-xl border transition-all hover:brightness-[0.98] focus-visible:ring-2 focus-visible:ring-[hsl(var(--focus-ring))] outline-none"
            style={{ background: bg, borderColor: borderCol }}
          >
            <div className="flex items-baseline gap-1.5 min-w-0">
              <span
                className="text-[10px] uppercase tracking-[0.14em] font-bold shrink-0"
                style={{ color: isSelected ? "rgba(255,255,255,0.85)" : (isToday ? "hsl(var(--primary))" : "hsl(var(--text-muted))") }}
              >
                {WEEKDAYS_NL[(d.getDay() + 6) % 7]}
              </span>
              <span
                className="font-display text-[16px] sm:text-[17px] font-bold tabular-nums leading-none"
                style={{ color: textStrong }}
              >
                {d.getDate()}
              </span>
              {isToday && !isSelected && (
                <span
                  className="w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ background: "hsl(var(--primary))" }}
                  aria-label="Vandaag"
                />
              )}
            </div>
            <div className="mt-2 text-[11.5px] tabular-nums leading-tight" style={{ color: textMuted }}>
              {scheduled > 0 ? (
                <>
                  <span className="font-bold" style={{ color: textStrong }}>{scheduled}</span>{" "}
                  {t("scheduled_short") || "ingepland"}
                </>
              ) : (
                <span className="italic opacity-70">{t("empty_day") || "leeg"}</span>
              )}
            </div>
            <div className="mt-1 flex items-center gap-1 flex-wrap min-h-[16px]">
              {st.open > 0 && (
                <span
                  className="inline-flex items-center px-1.5 rounded-full text-[10px] font-bold tabular-nums leading-[16px]"
                  style={{
                    background: isSelected ? "rgba(255,255,255,0.18)" : "hsl(var(--warning-soft))",
                    color: isSelected ? "white" : "hsl(var(--warning))",
                  }}
                  data-testid={`week-strip-open-${key}`}
                >
                  {st.open} {t("open") || "open"}
                </span>
              )}
              {st.conflicts > 0 && (
                <span
                  className="inline-flex items-center px-1.5 rounded-full text-[10px] font-bold tabular-nums leading-[16px]"
                  style={{
                    background: isSelected ? "rgba(255,255,255,0.18)" : "hsl(var(--danger-soft))",
                    color: isSelected ? "white" : "hsl(var(--danger))",
                  }}
                  data-testid={`week-strip-conflict-${key}`}
                >
                  {st.conflicts} {t("conflict_short") || "conflict"}
                </span>
              )}
              {st.drafts > 0 && (
                <span
                  className="inline-flex items-center px-1.5 rounded-full text-[10px] font-bold tabular-nums leading-[16px]"
                  style={{
                    background: isSelected ? "rgba(255,255,255,0.18)" : "hsl(var(--muted))",
                    color: isSelected ? "white" : "hsl(var(--text-muted))",
                  }}
                  data-testid={`week-strip-draft-${key}`}
                >
                  {st.drafts} {t("wijz_short") || "wijz."}
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
